import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ActiveQuestDto } from 'shared/interfaces/quest/active-quest.dto';
import { ClaimQuestResponseDto } from 'shared/interfaces/quest/claim-quest-response.dto';
import { QuestChestProgressDto } from 'shared/interfaces/quest/quest-chest-progress.dto';
import { questApi } from 'src/api/quest/quest';

type QuestsContextValue = {
  activeQuests: ActiveQuestDto[];
  isLoadingQuests: boolean;
  hasLoadedQuests: boolean;
  /** Last fetched cycle progress; kept while navigating so the quests page can render cached strip/chest state during refresh. */
  questChestProgress: QuestChestProgressDto | null;
  refreshQuests: () => Promise<ActiveQuestDto[]>;
  refreshQuestChestProgress: () => Promise<QuestChestProgressDto>;
  ensureQuestsLoaded: () => Promise<void>;
  claimQuestAndRefresh: (questId: number) => Promise<ClaimQuestResponseDto>;
  deleteQuestAndRefresh: (questId: number) => Promise<void>;
  markQuestNotificationsAsViewed: () => Promise<void>;
};

const QuestsContext = createContext<QuestsContextValue | undefined>(undefined);

export function QuestsProvider({ children }: { children?: ReactNode }) {
  const [activeQuests, setActiveQuests] = useState<ActiveQuestDto[]>([]);
  const [isLoadingQuests, setIsLoadingQuests] = useState(false);
  const [hasLoadedQuests, setHasLoadedQuests] = useState(false);
  const [questChestProgress, setQuestChestProgress] = useState<QuestChestProgressDto | null>(null);
  const refreshInFlightRef = useRef<Promise<ActiveQuestDto[]> | null>(null);

  const refreshQuests = useCallback(async (): Promise<ActiveQuestDto[]> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const request = (async () => {
      setIsLoadingQuests(true);
      try {
        const quests = await questApi.getActiveQuests();
        const normalizedQuests = Array.isArray(quests) ? quests : [];
        setActiveQuests(normalizedQuests);
        setHasLoadedQuests(true);
        return normalizedQuests;
      } finally {
        setIsLoadingQuests(false);
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = request;
    return request;
  }, []);

  const refreshQuestChestProgress = useCallback(async (): Promise<QuestChestProgressDto> => {
    const progress = await questApi.getQuestChestProgress();
    setQuestChestProgress(progress);
    return progress;
  }, []);

  const ensureQuestsLoaded = useCallback(async () => {
    if (hasLoadedQuests) {
      return;
    }
    await refreshQuests();
  }, [hasLoadedQuests, refreshQuests]);

  const claimQuestAndRefresh = useCallback(
    async (questId: number): Promise<ClaimQuestResponseDto> => {
      const claimResponse = await questApi.claimQuest(questId);
      await refreshQuests();
      return claimResponse;
    },
    [refreshQuests]
  );

  const deleteQuestAndRefresh = useCallback(
    async (questId: number): Promise<void> => {
      await questApi.deleteQuest(questId);
      await refreshQuests();
    },
    [refreshQuests]
  );

  const markQuestNotificationsAsViewed = useCallback(async () => {
    await questApi.markQuestNotificationsAsViewed();
  }, []);

  const value = useMemo(
    () => ({
      activeQuests,
      isLoadingQuests,
      hasLoadedQuests,
      questChestProgress,
      refreshQuests,
      refreshQuestChestProgress,
      ensureQuestsLoaded,
      claimQuestAndRefresh,
      deleteQuestAndRefresh,
      markQuestNotificationsAsViewed,
    }),
    [
      activeQuests,
      isLoadingQuests,
      hasLoadedQuests,
      questChestProgress,
      refreshQuests,
      refreshQuestChestProgress,
      ensureQuestsLoaded,
      claimQuestAndRefresh,
      deleteQuestAndRefresh,
      markQuestNotificationsAsViewed,
    ]
  );

  return <QuestsContext.Provider value={value}>{children}</QuestsContext.Provider>;
}

export function useQuestsContext(): QuestsContextValue {
  const context = useContext(QuestsContext);
  if (!context) {
    throw new Error('useQuestsContext must be used within QuestsProvider');
  }
  return context;
}
